import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

interface ConfirmDeleteDialogProps {
  readonly title: string;
  readonly message: string;
  /** Override the confirm button label; defaults to "Delete". */
  readonly confirmLabel?: string;
  /** Confirm button style; defaults to "destructive". Use "default" for non-destructive confirms. */
  readonly confirmVariant?: 'destructive' | 'default';
  readonly busy?: boolean;
  readonly onCancel: () => void;
  readonly onConfirm: () => void;
}

/**
 * Modal that gates destructive removal in the patient record (an
 * identifier, contact, address, measurement, document, etc). Always
 * pairs with a `mutation.mutate()` call inside `onConfirm`.
 */
export function ConfirmDeleteDialog({
  title,
  message,
  confirmLabel,
  confirmVariant = 'destructive',
  busy = false,
  onCancel,
  onConfirm,
}: ConfirmDeleteDialogProps): ReactElement {
  const { t } = useTranslation();
  return (
    <Dialog
      open
      onOpenChange={(next) => {
        if (!next) onCancel();
      }}
    >
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{message}</DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="ghost" onClick={onCancel} disabled={busy}>
            {t('ui.dialog.close')}
          </Button>
          <Button
            variant={confirmVariant}
            onClick={onConfirm}
            loading={busy}
            loadingText={t('patient.dirtyFooter.saving')}
          >
            {confirmLabel ?? t('ui.dialog.delete')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
